import { useLocale } from '@va/localization';
import { DateInputContextProvider, DateTooltip, fontWeights, Paragraph, paragraphSizes } from '@va/ui/design-system';
import { getLocalizedDate } from '@va/util/helpers';
import classNames from 'classnames';
import { Dayjs } from 'dayjs';
import { useCallback, useState } from 'react';
import { FilterBlock } from '../../FilterBlock';
import { useFiltersContext } from '../../filters-context';
import {
  DATE_FILTER_VALUE_FORMAT,
  DateFilter,
  DateFilterInputProps,
  DateFilterValue,
  parseDateFilterValue,
} from './DateFilter';

type DateFilterAppliedProps = {
  filter: DateFilter;
  viewOnly?: boolean;
  disableEditing?: boolean;
  isGrouped?: boolean;
  size?: 'small' | 'medium' | 'large';
} & DateFilterInputProps;

export const DateFilterApplied = ({
  filter,
  viewOnly,
  disableEditing,
  isGrouped,
  pickerConfig,
  size = 'large',
}: DateFilterAppliedProps) => {
  const { updateAppliedFilter } = useFiltersContext();
  const { values, id, operator, operatorOptions, label } = filter;

  const filterValue = values[0];
  const start = parseDateFilterValue(filterValue?.start);
  const end = parseDateFilterValue(filterValue?.end);

  const onChange = useCallback(
    (newValue: Partial<DateFilterValue>) => {
      updateAppliedFilter(id, { values: [{ ...filterValue, ...newValue }] });
    },
    [filterValue, id, updateAppliedFilter],
  );

  return (
    <FilterBlock
      filterId={id}
      fieldName={label}
      operator={operator}
      operatorOptions={operatorOptions}
      hideDeleteButton={viewOnly}
      disabled={disableEditing}
      size={size}
      isGrouped={isGrouped}
      input={
        <div
          className={classNames('flex items-center gap-2 h-full rounded-r-12', {
            '!bg-transparent': viewOnly,
          })}
        >
          <AppliedDatePicker
            value={start}
            disabled={disableEditing}
            pickerConfig={{ ...pickerConfig, maxDate: end ?? pickerConfig?.maxDate }}
            onChange={(val) => onChange({ start: val.format(DATE_FILTER_VALUE_FORMAT) })}
          />
          <div>-</div>
          <AppliedDatePicker
            value={end}
            className='rounded-12'
            disabled={disableEditing}
            pickerConfig={{ ...pickerConfig, minDate: start ?? pickerConfig?.minDate }}
            onChange={(val) => onChange({ end: val.format(DATE_FILTER_VALUE_FORMAT) })}
          />
        </div>
      }
    />
  );
};

const AppliedDatePicker = ({
  value,
  onChange,
  pickerConfig,
  disabled,
  className,
}: {
  value: Dayjs | undefined;
  onChange: (value: Dayjs) => void;
  pickerConfig?: DateFilterInputProps['pickerConfig'];
  disabled?: boolean;
  className?: string;
}) => {
  const [open, setOpen] = useState(false);
  const { locale } = useLocale();

  const label = (
    <Paragraph size={paragraphSizes.tiny} weight={fontWeights.medium}>
      {value ? getLocalizedDate(value.unix(), locale) : '-'}
    </Paragraph>
  );

  if (disabled) return <div className={classNames('input-wrapper !bg-transparent', className)}>{label}</div>;

  return (
    <DateInputContextProvider
      value={value}
      pickerConfig={pickerConfig}
      onChange={(val) => {
        onChange(val);
        setOpen(false);
      }}
    >
      <DateTooltip open={open} onOpenChange={setOpen}>
        <button type='button' className={classNames('input-wrapper', className)}>
          {label}
        </button>
      </DateTooltip>
    </DateInputContextProvider>
  );
};
